import { gql } from '@apollo/client'
import * as React from 'react'
import styled, { css } from 'styled-components'
import { tourTypeToColor } from '../../../../common/src/components/Icon'
import { TypeToggle, TypeToggleInner } from '../../../../common/src/components/core'
import { ColorName, Colors } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import { addFeedback } from '../../../../common/src/feedback'
import keyboard, { KeyAction } from '../../../../common/src/keyboard'
import { TourType } from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { TourTypeWithIcon } from '../components/TourTypeWithIcon'

const setThreadPreferredTourTypeMutation = gql`
  mutation setThreadPreferredTourType($threadId: Int!, $preferredTourType: TourType) {
    setThreadPreferredTourType(threadId: $threadId, preferredTourType: $preferredTourType) {
      id
      unitTypesInterested
    }
  }
`

function setThreadPreferredTourType(threadId: number, preferredTourType: TourType) {
  apolloWebClient
    .mutate({
      mutation: setThreadPreferredTourTypeMutation,
      variables: { threadId, preferredTourType },
    })
    .then(() => addFeedback('Set tour type'))
    .catch(handleError)
}

export function TourTypesChooser({
  threadId,
  tourTypeOptions,
  preferredTourType,
  setStaleThread,
}: {
  threadId: number
  tourTypeOptions: TourType[]
  preferredTourType?: TourType | null
  setStaleThread: (stale: boolean) => void
}) {
  const options =
    preferredTourType == null ? tourTypeOptions : Array.from(new Set([preferredTourType, ...tourTypeOptions]))

  const onKeyboardBound = (action: KeyAction) => {
    if (action === KeyAction.toggleVirtualTour && tourTypeOptions.includes(TourType.VIRTUAL)) {
      setThreadPreferredTourType(threadId, TourType.VIRTUAL)
    } else if (action === KeyAction.toggleInPersonTour && tourTypeOptions.includes(TourType.IN_PERSON)) {
      setThreadPreferredTourType(threadId, TourType.IN_PERSON)
    } else if (action === KeyAction.toggleSelfGuidedTour && tourTypeOptions.includes(TourType.SELF_GUIDED)) {
      setThreadPreferredTourType(threadId, TourType.SELF_GUIDED)
    }
  }

  React.useEffect(() => {
    keyboard.addListener(onKeyboardBound)

    return () => keyboard.removeListener(onKeyboardBound)
  }, [threadId])

  return (
    <>
      {options.map(tt => (
        <TourTypesOption
          threadId={threadId}
          tourType={tt}
          key={tt}
          isSelected={preferredTourType === tt}
          tourTypeOptions={tourTypeOptions}
          setStaleThread={setStaleThread}
        />
      ))}
    </>
  )
}

function TourTypesOption({
  threadId,
  tourType,
  isSelected,
  tourTypeOptions,
  setStaleThread,
}: {
  threadId: number
  tourType: TourType
  isSelected: boolean
  tourTypeOptions: TourType[]
  setStaleThread: (stale: boolean) => void
}) {
  const onClick = React.useCallback(() => {
    if (isSelected) {
      return
    }

    if (setStaleThread) {
      setStaleThread(true)
    }

    setThreadPreferredTourType(threadId, tourType)
  }, [isSelected, setStaleThread, threadId, tourType])

  const tourTypeInOptions = tourTypeOptions.includes(tourType)

  return (
    <TourTypeToggle onClick={onClick} isSelected={isSelected} tourType={tourType}>
      <TourTypeWithIcon tourType={tourType}></TourTypeWithIcon>
      <TourTypeToggleInner
        isSelected={isSelected}
        isAvailable={tourTypeInOptions}
        tourType={tourType}
        title={tourTypeInOptions ? undefined : 'tour type no longer exists on property'}
      >
        <TourTypeWithIcon tourType={tourType}></TourTypeWithIcon>
      </TourTypeToggleInner>
    </TourTypeToggle>
  )
}

/**
 * It looks a little underwhelming when the "tour type chooser" is set to IN_PERSON
 * This method overrides the standard "silver" color for IN_PERSON tours
 */
function tourTypeToColorEmphasizingInPerson(tourType: TourType): ColorName {
  if (tourType === TourType.IN_PERSON) {
    return Colors.snow as ColorName
  }
  return tourTypeToColor(tourType)
}

/**
 * Styled the same as a normal TypeToggle except that the entire element is colored
 * when the tourType is selected.
 */
const TourTypeToggle = styled(TypeToggle)`
  ${(props: { isSelected: boolean; tourType: TourType }) => css`
    border-color: ${props.isSelected ? tourTypeToColor(props.tourType) : Colors.ink};
  `};
`

const TourTypeToggleInner = styled(TypeToggleInner)`
  ${(props: { isSelected?: boolean; isAvailable?: boolean; tourType: TourType }) => css`
    color: ${props.isSelected ? tourTypeToColorEmphasizingInPerson(props.tourType) : Colors.midway};
  `};
`
